import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import { notification, Select, Button, Typography, Space, Alert, Divider } from 'antd';
import { SwapOutlined } from '@ant-design/icons';
import { t } from '../../i18n/i18n';
import Modal from '../Modal/Modal';
import { PartnerWorkspaceRole } from '../../constants/enums';
import { determineIconType } from '../../utils/determineIconWorkspace/determineIconWorkspace';
import { getPlanByWorkspaceId, moveScreeningToWorkspace } from '../../API/services/alkymersService';
import { HOME } from '../../constants/routes';
import { transferScreeningDraft } from '../../API/services/screeningService';
import { debounce } from 'lodash';
import './ModalMoveScreening.scss';

const { Title, Text } = Typography;

const ModalMoveScreening = ({ showModal, setShowModal, data, reloadSearches }) => {
  const [workspaceSelected, setWorkspaceSelected] = useState('');
  const [workspaceCompanySelected, setWorkspaceCompanySelected] = useState('');
  const { workspacesList, currentWorkspace } = useSelector((state) => state.auth);
  const [workspaceOptions, setWorkspaceOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [disableButton, setDisabledButton] = useState(true);
  const [creditLeftWarning, setCreditLeftWarning] = useState(null);
  const [workspaceToName, setWorkspaceToName] = useState(t('WORKSPACE_TO'));
  const { Option } = Select;
  const navigate = useHistory();

  useEffect(() => {
    const workspacesAuthorize = workspacesList.filter(
      (w) =>
        [PartnerWorkspaceRole.OWNER, PartnerWorkspaceRole.ADMINISTRATOR].includes(w.role) &&
        w.id !== currentWorkspace
    );
    setWorkspaceOptions(workspacesAuthorize);
  }, [workspacesList]);

  const onClickMove = async () => {
    setIsLoading(true);
    try {
      const options = {
        workspaceId: workspaceSelected,
      };
      if (data?.isDraft) {
        await transferScreeningDraft(data.id, {
          ...options,
          ...(workspaceCompanySelected && { companyId: workspaceCompanySelected }),
        });
      } else {
        await moveScreeningToWorkspace(data.id, options);
      }

      notification.open({
        message: t('SCREENING_TRANSFERED'),
        type: 'success',
      });
      setShowModal(false);
      if (reloadSearches !== undefined) {
        reloadSearches();
      } else {
        navigate.push(HOME);
      }
    } catch (e) {
      const message = e.response.data.message.toLowerCase();
      if (e.response.status > 400 && (message.includes('plan') || message.includes('workspace'))) {
        notification.open({
          message: t('ERROR_IN_WORKSPACE_PLANS'),
          type: 'error',
        });
      } else {
        notification.open({
          message: t('SCREENING_NOT_TRANSFERED_ERROR'),
          type: 'error',
        });
      }
      setIsLoading(false);
    }
  };

  const onSelectWorkspace = async (id) => {
    try {
      setDisabledButton(true);
      const workspacePlanInfo = await getPlanByWorkspaceId(id);
      const creditLeftAmount =
        workspacePlanInfo.screeningsAmount - workspacePlanInfo.screeningsUsed;
      const warningText = creditLeftAmount ?? t('LIMIT_CREDITS');
      setCreditLeftWarning(warningText);
    } catch (e) {
      setCreditLeftWarning(t('LIMIT_CREDITS'));
    }
    const actualWorkspace = workspaceOptions.find((x) => x.id === id);
    setWorkspaceToName(actualWorkspace.name ?? t('WORSKPACE_NAME'));
    setWorkspaceCompanySelected(actualWorkspace.companyId);
    setWorkspaceSelected(id);
    setDisabledButton(false);
  };

  const handleClickMove = debounce(() => {
    onClickMove();
  }, 500);

  return (
    <>
      <Modal
        setShow={() => setShowModal(false)}
        show={showModal}
        data-testid="card-screening-search-modal"
        className="modal-move-screening"
        showCloseButton
        minWidth={false}
      >
        <div className="move-screening-content" id="move-screening-container">
          <div className="move-screening-header">
            <Title level={3} className="move-screening-title">
              {t('MOVE_SCREENING_TITLE')}
            </Title>
          </div>

          <Divider />

          <Alert
            className="move-screening-alert"
            message={
              <div className="alert-content">
                <Text strong>{t('MOVE_SCREENING_WARNING')}</Text>
                <ol className="warning-list">
                  <li>
                    <Text>{t('MOVE_SCREENING_WARNING_FIRST')}</Text>
                  </li>
                  <li>
                    <Text>{t('MOVE_SCREENING_WARNING_SECOND')}</Text>
                  </li>
                </ol>
              </div>
            }
          />

          {creditLeftWarning && !disableButton && (
            <Alert
              type="warning"
              showIcon
              className="credit-warning-alert"
              message={
                <Text>
                  {t('MOVE_SCREENING_WARNING_THIRD_ALERT')}
                  <Text strong>{` ${workspaceToName} `}</Text>
                  {t('MOVE_SCREENING_WARNING_THIRD_INFO').replace('XX', creditLeftWarning)}
                </Text>
              }
            />
          )}

          <div className="workspace-selection-section">
            <Text strong className="selection-label">
              {t('SELECT_WORKSPACE_TO_MOVE')}
            </Text>
            <Select
              size="large"
              placeholder={t('SELECT_ONE_WORKSPACE')}
              onChange={(value) => {
                onSelectWorkspace(value);
              }}
              value={workspaceSelected || undefined}
              showArrow
              className="workspace-select"
              getPopupContainer={() => document.getElementById('move-screening-container')}
            >
              {(workspaceOptions ?? [])?.map((x) => (
                <Option key={x.id} value={x.id}>
                  <Space>
                    {determineIconType(x)}
                    <Text strong>
                      {x.name} {x.companyName ? `| ${x.companyName}` : ''}
                    </Text>
                  </Space>
                </Option>
              ))}
            </Select>
          </div>

          <Divider />

          <div className="move-screening-actions">
            <Button size="large" onClick={() => setShowModal(false)}>
              {t('CANCEL')}
            </Button>
            <Button
              size="large"
              type="primary"
              icon={<SwapOutlined />}
              onClick={handleClickMove}
              disabled={disableButton}
              loading={isLoading}
            >
              {t('CONFIRM_MOVE_SCREENING')}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ModalMoveScreening;
